<template>
  <Loader :value="promise" class="shop-floorplan" :data-title="$t('youAreHere')">
    <div class="shop-floorplan__aspect" :style="style">
      <div ref="map" class="shop-floorplan__map content"></div>
    </div>
  </Loader>
</template>

<script>
import Leaflet from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { getDimensions } from './utils'

import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    image: String,
  },
  watch: {
    image: {
      handler: 'init',
      immediate: true,
    },
    value: {
      handler: 'render',
    },
  },
  data() {
    return {
      promise: null,
      map: null,
      layer: null,
      dimensions: null,
    }
  },
  computed: {
    ratio() {
      if (!this.dimensions) return 0
      return this.dimensions.height / this.dimensions.width
    },
    style() {
      if (!this.dimensions) return {}

      let maxHeight = 450
      let maxWidth = Math.min(
        (this.dimensions.width / this.dimensions.height) * maxHeight,
        this.dimensions.width,
      )

      return {
        '--map-ratio': `${this.ratio * 100}%`,
        maxWidth: `${maxWidth}px`,
      }
    },
  },
  methods: {
    render() {
      if (!this.layer) return
      this.layer.clearLayers()

      let primary = getComputedStyle(document.documentElement).getPropertyValue('--primary')

      this.value.forEach(item => {
        let layer = Leaflet.GeoJSON.geometryToLayer(item, {
          fillColor: primary,
          fillOpacity: 0.3,
          color: primary,
          weight: 2,
        })

        this.layer.addLayer(layer)
      })
    },
    destroyMap() {
      if (!this.map) return

      this.map.off()
      this.map.remove()
    },
    init() {
      if (!this.image) return
      this.destroyMap()

      this.promise = getDimensions(this.image)
        .then(dimensions => {
          this.dimensions = dimensions
          return new Promise(resolve => {
            this.$nextTick(() => resolve(dimensions))
          })
        })
        .then(dimensions => {
          if (!this.$refs.map) return

          Leaflet.CRS.CustomZoom = Leaflet.extend({}, Leaflet.CRS.Simple)

          this.map = Leaflet.map(this.$refs.map, {
            fullscreenControl: true, // fullscreen from plugin https://github.com/Leaflet/Leaflet.fullscreen
            minZoom: -5,
            maxZoom: 3,
            center: [0, 0],
            zoom: 0,
            zoomSnap: 0.01,
            scrollWheelZoom: false,
            maxBoundsViscosity: 0.9,
            crs: Leaflet.CRS.CustomZoom,
          })

          let southWest = this.map.unproject([0, dimensions.height], 0)
          let northEast = this.map.unproject([dimensions.width, 0], 0)
          let bounds = new Leaflet.LatLngBounds(southWest, northEast)

          let overlay = Leaflet.imageOverlay(this.image, bounds).addTo(this.map)

          this.map.fitBounds(bounds, { animate: false })
          this.map.setMaxBounds(bounds, { animate: false })

          this.map.on('fullscreenchange', () => {
            this.map.fitBounds(bounds, { animate: false })
          })

          this.layer = Leaflet.featureGroup()
          this.map.addLayer(this.layer)

          this.render()
        })
    },
  },
  beforeDestroy() {
    this.destroyMap()
  },
}
</script>

<style lang="scss">
.shop-floorplan {
  display: flex;
  min-height: 150px;

  $spacing: 2rem;

  &__aspect {
    width: 100%;
    @include aspect-ratio(1, 1);
    background: var(--shop-header-background);
    border-radius: var(--border-radius);
    overflow: clip;

    @include respond-below('phone') {
      margin-bottom: $spacing * 1.6;
    }

    &:before {
      padding-top: var(--map-ratio);
    }
  }

  &__bottom-right,
  &__bottom-left {
    position: absolute;
    bottom: $spacing;
    z-index: 999;

    @include respond-below('phone') {
      bottom: $spacing * -1.5;
    }
  }

  &__bottom-right {
    right: $spacing;

    @include respond-below('phone') {
      right: 0;
    }
  }

  &__bottom-left {
    left: $spacing;

    @include respond-below('phone') {
      left: 0;
    }
  }

  &__map {
    border-radius: $border-radius;
    overflow: hidden;
  }

  .leaflet-control-attribution {
    display: none;
  }

  .leaflet-container {
    background-color: rgba(255, 0, 0, 0);
  }

  .elder-loader__element {
    z-index: 9999;
  }

  .leaflet-control {
    margin-left: 5px;

    &:first-child {
      margin-top: 5px;
    }
  }

  .leaflet-interactive {
    animation: pulse infinite ease-in-out 2000ms;
    cursor: inherit !important;

    @keyframes pulse {
      0%,
      100% {
        fill: #ffffff;
      }
      50% {
        fill: var(--primary);
      }
    }
  }
}
</style>
